import React, { useState, useEffect } from 'react';
import { Project } from '../types';
import { ProjectFormModal } from '../components/ProjectFormModal';
import { BatchPreviewModal } from '../components/BatchPreviewModal'; // Import new modal
import { projectService } from '../services/db';
import { auth } from '../services/firebase';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut, User } from 'firebase/auth';
import { Lock, Plus, Search, Trash2, Edit, LogOut, Eye, Youtube, RefreshCw, ArrowUp, ArrowDown, Activity, ChevronUp, ChevronDown, AlertTriangle, ImagePlus, Mail, Smartphone, Monitor, BarChart3, Zap, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { testYouTubeConnection } from '../services/youtubeService';

interface AdminPageProps {
  projects: Project[];
  onAddProject: (p: Project) => Promise<void>;
  onUpdateProject: (p: Project) => Promise<void>;
  onDeleteProject: (id: string) => Promise<void>;
  onSync?: () => void;
  isSyncing?: boolean;
}

type SortKey = 'id' | 'title' | 'category' | 'createdAt' | 'views';

export const AdminPage: React.FC<AdminPageProps> = ({ projects, onAddProject, onUpdateProject, onDeleteProject, onSync, isSyncing }) => {
  const navigate = useNavigate();
  
  // Auth State
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // Dashboard State
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({ key: 'id', direction: 'desc' });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // New Batch Modal State
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);

  // Delete Confirmation State
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);

  // Debug State
  const [apiStatus, setApiStatus] = useState<{success: boolean, message: string} | null>(null);
  const isLive = projectService.isLive();

  // Monitor Auth State
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Authentication Handler
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      console.error("Login failed", error);
      setLoginError('Invalid email or password.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  // Test API Function
  const handleTestApi = async () => {
      setApiStatus({ success: false, message: 'Testing connection...' });
      const result = await testYouTubeConnection();
      setApiStatus(result);
  };

  // CRUD Handlers
  const handleSave = async (projectData: Partial<Project>) => {
    setIsSaving(true);
    try {
      if (editingProject) {
        // Update existing
        await onUpdateProject({ ...editingProject, ...projectData } as Project);
      } else {
        // Create new
        const newProject: Project = {
          ...projectData as Project,
          // ID is now handled by the form (Day #), or falls back to timestamp if empty
          id: projectData.id || Date.now().toString(),
          createdAt: Date.now(),
          views: 0
        };
        await onAddProject(newProject);
      }
      setIsFormOpen(false);
      setEditingProject(null);
    } catch (e: any) {
      alert("Error saving project: " + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const openAddModal = () => {
    setEditingProject(null);
    setIsFormOpen(true);
  };

  const openEditModal = (project: Project) => {
    setEditingProject(project);
    setIsFormOpen(true);
  };

  const requestDelete = (id: string) => {
    setProjectToDelete(id);
  };

  const confirmDelete = async () => {
    if (projectToDelete) {
      setIsSaving(true);
      try {
        await onDeleteProject(projectToDelete);
        setProjectToDelete(null);
      } catch (e: any) {
        alert("Error deleting project: " + e.message);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const requestSort = (key: SortKey) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredProjects = [...projects]
    .filter(p => 
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      p.category.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const { key, direction } = sortConfig;
      let valA: any = a[key];
      let valB: any = b[key];

      if (key === 'id') {
          valA = parseInt(a.id) || 0;
          valB = parseInt(b.id) || 0;
      }
      if (key === 'views') {
          valA = a.views || 0;
          valB = b.views || 0;
      }
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });

  const maxId = projects.reduce((max, p) => {
     const numId = parseInt(p.id);
     return !isNaN(numId) && numId > max ? numId : max;
  }, 0);
  const nextIdSuggestion = (maxId + 1).toString();

  // DASHBOARD STATS CALCULATION
  const totalViews = projects.reduce((acc, curr) => acc + (curr.views || 0), 0);
  const totalProjects = projects.length;
  const linkedVideos = projects.filter(p => p.youtubeVideoId).length;
  const missingPreviewsCount = projects.filter(p => !p.desktopPreviewUrl || !p.mobilePreviewUrl).length;

  const SortIcon = ({ colKey }: { colKey: SortKey }) => {
      if (sortConfig.key !== colKey) return <div className="w-3 h-3 opacity-0 group-hover:opacity-30"><ArrowDown size={12} /></div>;
      return sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const formatLargeNumber = (num: number) => {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
      return num.toLocaleString();
  };

  if (authLoading) {
     return (
        <div className="min-h-screen flex items-center justify-center bg-indigo-50 dark:bg-black">
           <RefreshCw className="animate-spin text-primary" size={32} />
        </div>
     );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-indigo-50 dark:bg-black">
         <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
         
         <div className="w-full max-w-md glass-panel p-8 rounded-2xl shadow-2xl animate-fade-in relative z-10">
            <div className="flex justify-center mb-6">
               <div className="w-16 h-16 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary border border-primary/20 shadow-[0_0_30px_rgba(99,102,241,0.3)]">
                  <Lock size={32} />
               </div>
            </div>
            
            <h2 className="text-2xl font-bold text-center text-slate-800 dark:text-white mb-2">Admin Dashboard</h2>
            <p className="text-center text-slate-500 dark:text-slate-400 text-sm mb-8">Sign in with your Firebase account.</p>
            
            <form onSubmit={handleLogin} className="space-y-4">
               <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                    type="email" 
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full bg-white/50 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-lg py-3 pl-10 pr-4 text-slate-900 dark:text-white focus:border-primary outline-none transition-all placeholder:text-slate-400"
                    placeholder="admin@iqodex.com"
                    autoFocus
                    required
                  />
               </div>
               <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                    type="password" 
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full bg-white/50 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-lg py-3 pl-10 pr-4 text-slate-900 dark:text-white focus:border-primary outline-none transition-all placeholder:text-slate-400"
                    placeholder="••••••••"
                    required
                  />
               </div>
               
               {loginError && (
                 <p className="text-red-500 text-xs text-center">{loginError}</p>
               )}

               <button 
                 type="submit" 
                 className="w-full bg-primary hover:bg-primaryHover text-white font-bold py-3 rounded-lg transition-all shadow-lg shadow-primary/25"
               >
                 Sign In
               </button>
            </form>
            <button onClick={() => navigate('/')} className="w-full mt-4 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
               ← Return to Portfolio
            </button>
         </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-slate-800 dark:text-slate-200 font-sans selection:bg-primary/30">
       
       {/* Top Navbar */}
       <header className="sticky top-0 z-30 bg-white/80 dark:bg-black/80 backdrop-blur-lg border-b border-slate-200 dark:border-white/5 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold shrink-0">A</div>
             <div>
                <h1 className="font-bold text-sm md:text-lg text-slate-800 dark:text-white tracking-tight leading-none">IQODEX <span className="text-slate-500 font-normal hidden sm:inline">Manager</span></h1>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-orange-500'}`}></span>
                    <span className="text-[9px] text-slate-500 uppercase font-semibold tracking-wider flex items-center gap-1">
                        {isLive ? 'Firebase Live' : 'Local Storage'}
                    </span>
                </div>
             </div>
          </div>
          
          <div className="flex items-center gap-2 md:gap-3">
             {/* BATCH GENERATOR BUTTON */}
             <button
                onClick={() => setIsBatchModalOpen(true)}
                className="flex items-center gap-1.5 md:gap-2 text-[10px] md:text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors bg-blue-100 dark:bg-blue-500/10 hover:bg-blue-200 dark:hover:bg-blue-500/20 px-2.5 py-1.5 md:px-3 rounded-full border border-blue-200 dark:border-blue-500/20 whitespace-nowrap"
             >
                <ImagePlus size={12} />
                <span className="hidden sm:inline">Audit Previews ({missingPreviewsCount})</span>
             </button>

             {/* DEBUG BUTTON */}
             <button 
                onClick={handleTestApi}
                className="flex items-center gap-1.5 md:gap-2 text-[10px] md:text-xs font-medium text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300 transition-colors bg-yellow-100 dark:bg-yellow-400/10 hover:bg-yellow-200 dark:hover:bg-yellow-400/20 px-2.5 py-1.5 md:px-3 rounded-full border border-yellow-200 dark:border-yellow-400/20 whitespace-nowrap"
             >
                <Activity size={12} />
                <span className="hidden sm:inline">Test YT API</span>
             </button>

             {onSync && (
                 <button 
                    onClick={onSync} 
                    disabled={isSyncing}
                    className="flex items-center gap-1.5 md:gap-2 text-[10px] md:text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors bg-green-100 dark:bg-green-500/10 hover:bg-green-200 dark:hover:bg-green-500/20 px-2.5 py-1.5 md:px-3 rounded-full border border-green-200 dark:border-green-500/20 disabled:opacity-50 whitespace-nowrap"
                 >
                   <RefreshCw size={12} className={isSyncing ? "animate-spin" : ""} />
                   <span>Sync</span>
                 </button>
             )}
             
             <div className="h-4 w-px bg-slate-200 dark:bg-white/10 mx-1"></div>
             
             <button onClick={() => navigate('/')} className="p-2 md:px-3 md:py-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors flex items-center gap-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5" title="View Site">
                <Eye size={14} />
             </button>
             
             <button onClick={handleLogout} className="p-2 md:px-3 md:py-1.5 flex items-center gap-2 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors bg-red-100 dark:bg-red-500/10 rounded-full border border-red-200 dark:border-red-500/20 hover:bg-red-200 dark:hover:bg-red-500/20" title="Logout">
               <LogOut size={14} />
             </button>
          </div>
       </header>

       <main className="container mx-auto px-4 py-6 md:py-8 max-w-7xl">
          
          {/* DASHBOARD STATS */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-fade-in">
              {/* Total Views */}
              <div className="glass-panel p-5 rounded-2xl flex flex-col relative overflow-hidden group">
                  <div className="flex justify-between items-start mb-4 relative z-10">
                      <div className="p-2 bg-red-500/10 text-red-500 rounded-lg">
                          <BarChart3 size={20} />
                      </div>
                      <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Total Views</span>
                  </div>
                  <div className="text-3xl font-black text-slate-800 dark:text-white mb-1 relative z-10">
                      {formatLargeNumber(totalViews)}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                      Across all YouTube videos
                  </div>
                  <div className="absolute -bottom-4 -right-4 text-red-500/5 rotate-12 transform scale-150 transition-transform group-hover:scale-125 duration-500">
                      <Youtube size={120} />
                  </div>
              </div>

              {/* Total Projects */}
              <div className="glass-panel p-5 rounded-2xl flex flex-col relative overflow-hidden group">
                  <div className="flex justify-between items-start mb-4 relative z-10">
                      <div className="p-2 bg-blue-500/10 text-blue-500 rounded-lg">
                          <Zap size={20} />
                      </div>
                      <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Projects</span>
                  </div>
                  <div className="text-3xl font-black text-slate-800 dark:text-white mb-1 relative z-10">
                      {totalProjects} <span className="text-sm font-medium text-slate-400">/ 365</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-200 dark:bg-white/10 rounded-full mt-2 overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: `${(totalProjects/365)*100}%` }}></div>
                  </div>
              </div>

               {/* YouTube Connected */}
               <div className="glass-panel p-5 rounded-2xl flex flex-col relative overflow-hidden group">
                  <div className="flex justify-between items-start mb-4 relative z-10">
                      <div className="p-2 bg-purple-500/10 text-purple-500 rounded-lg">
                          <Youtube size={20} />
                      </div>
                      <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Connected</span>
                  </div>
                  <div className="text-3xl font-black text-slate-800 dark:text-white mb-1 relative z-10">
                      {linkedVideos}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                      Projects linked to YouTube
                  </div>
              </div>

              {/* Previews Status */}
              <div className="glass-panel p-5 rounded-2xl flex flex-col relative overflow-hidden group">
                  <div className="flex justify-between items-start mb-4 relative z-10">
                      <div className="p-2 bg-green-500/10 text-green-500 rounded-lg">
                          <ImagePlus size={20} />
                      </div>
                      <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Previews</span>
                  </div>
                  <div className="text-3xl font-black text-slate-800 dark:text-white mb-1 relative z-10">
                      {totalProjects - missingPreviewsCount}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                      {missingPreviewsCount > 0 ? `${missingPreviewsCount} missing screenshots` : 'All projects have previews'}
                  </div>
              </div>
          </div>

          {/* Messages Area */}
          {apiStatus && (
              <div className="mb-6 space-y-2">
                 {apiStatus && (
                    <div className={`p-4 rounded-xl border ${apiStatus.success ? 'bg-green-100 border-green-200 text-green-700 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-300' : 'bg-red-100 border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-300'} text-xs md:text-sm flex items-start gap-3`}>
                        {apiStatus.success ? <div className="p-1 bg-green-500 rounded-full mt-0.5"></div> : <div className="p-1 bg-red-500 rounded-full mt-0.5"></div>}
                        <div>
                            <strong className="block mb-1 font-bold">{apiStatus.success ? 'Success' : 'Connection Failed'}</strong>
                            {apiStatus.message}
                        </div>
                    </div>
                 )}
              </div>
          )}

          {/* Controls */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
             <div className="flex gap-4 w-full md:w-auto md:flex-1">
                 <div className="relative w-full md:w-auto md:flex-1 md:max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                    <input 
                      type="text" 
                      placeholder="Search projects..." 
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-lg py-2.5 pl-9 pr-4 text-xs md:text-sm text-slate-800 dark:text-white focus:border-primary outline-none focus:ring-1 focus:ring-primary/50"
                    />
                 </div>
             </div>

             <button 
               onClick={openAddModal}
               disabled={isSaving}
               className="w-full md:w-auto bg-primary hover:bg-primaryHover text-white px-5 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-all shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               {isSaving ? <RefreshCw className="animate-spin" size={16} /> : <Plus size={16} />}
               {isSaving ? 'Saving...' : 'Add New Project'}
             </button>
          </div>

          {/* Data Table */}
          <div className="glass-panel rounded-xl overflow-hidden shadow-2xl">
             <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[700px]">
                   <thead>
                      <tr className="bg-slate-5 dark:bg-white/5 border-b border-slate-200 dark:border-white/5 text-[10px] md:text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                         <th onClick={() => requestSort('id')} className="px-4 md:px-6 py-3 md:py-4 cursor-pointer hover:text-slate-900 dark:hover:text-white group transition-colors">
                            <div className="flex items-center gap-1">Day <SortIcon colKey="id" /></div>
                         </th>
                         <th onClick={() => requestSort('title')} className="px-4 md:px-6 py-3 md:py-4 cursor-pointer hover:text-slate-900 dark:hover:text-white group transition-colors">
                            <div className="flex items-center gap-1">Project <SortIcon colKey="title" /></div>
                         </th>
                         <th className="px-4 md:px-6 py-3 md:py-4">Stats</th>
                         <th className="px-4 md:px-6 py-3 md:py-4">Previews</th>
                         <th onClick={() => requestSort('createdAt')} className="px-4 md:px-6 py-3 md:py-4 cursor-pointer hover:text-slate-900 dark:hover:text-white group transition-colors">
                            <div className="flex items-center gap-1">Date <SortIcon colKey="createdAt" /></div>
                         </th>
                         <th className="px-4 md:px-6 py-3 md:py-4 text-center">Actions</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-200 dark:divide-white/5 text-[11px] md:text-sm">
                      {filteredProjects.map((project) => (
                         <tr key={project.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors group">
                            <td className="px-4 md:px-6 py-3 md:py-4 text-slate-500 font-mono font-bold">
                               #{project.id}
                            </td>
                            <td className="px-4 md:px-6 py-3 md:py-4">
                               <div className="flex items-center gap-2">
                                  {project.youtubeVideoId ? (
                                      <Youtube size={14} className="text-red-500" title="Linked to YouTube" />
                                  ) : (
                                      <Youtube size={14} className="text-slate-300 dark:text-slate-600" title="No Video" />
                                  )}
                                  <div>
                                      <div className="font-medium text-slate-800 dark:text-slate-200 truncate max-w-[150px]">{project.title}</div>
                                      <div className="text-[10px] text-slate-500">{project.category}</div>
                                  </div>
                               </div>
                            </td>
                            
                            {/* STATS COL */}
                            <td className="px-4 md:px-6 py-3 md:py-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                                        <Eye size={12} />
                                        <span className="font-mono">{formatLargeNumber(project.views || 0)}</span>
                                    </div>
                                </div>
                            </td>

                            {/* PREVIEW STATUS COL */}
                            <td className="px-4 md:px-6 py-3 md:py-4">
                               <div className="flex gap-2">
                                  {project.desktopPreviewUrl ? (
                                      <div className="p-1.5 bg-green-500/10 text-green-500 rounded border border-green-500/20" title="Desktop Ready"><Monitor size={14} /></div>
                                  ) : (
                                      <div className="p-1.5 bg-red-500/10 text-red-500 rounded border border-red-500/20 opacity-50" title="Missing Desktop"><Monitor size={14} /></div>
                                  )}
                                  {project.mobilePreviewUrl ? (
                                      <div className="p-1.5 bg-green-500/10 text-green-500 rounded border border-green-500/20" title="Mobile Ready"><Smartphone size={14} /></div>
                                  ) : (
                                      <div className="p-1.5 bg-red-500/10 text-red-500 rounded border border-red-500/20 opacity-50" title="Missing Mobile"><Smartphone size={14} /></div>
                                  )}
                               </div>
                            </td>
                            <td className="px-4 md:px-6 py-3 md:py-4 text-[10px] md:text-xs text-slate-500 font-mono">
                               {new Date(project.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 md:px-6 py-3 md:py-4">
                               <div className="flex items-center justify-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => openEditModal(project)} className="p-1.5 hover:bg-blue-100 dark:hover:bg-blue-500/20 hover:text-blue-600 dark:hover:text-blue-400 rounded transition-colors" title="Edit">
                                     <Edit size={14} />
                                  </button>
                                  <button 
                                    onClick={() => requestDelete(project.id)} 
                                    className="p-1.5 hover:bg-red-100 dark:hover:bg-red-500/20 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors" 
                                    title="Delete"
                                  >
                                     <Trash2 size={14} />
                                  </button>
                               </div>
                            </td>
                         </tr>
                      ))}
                   </tbody>
                </table>
             </div>
          </div>
       </main>

       {/* Edit/Add Modal */}
       <ProjectFormModal 
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleSave}
          initialData={editingProject}
          nextIdSuggestion={nextIdSuggestion}
       />

        {/* Batch Preview Audit Modal */}
        <BatchPreviewModal
            isOpen={isBatchModalOpen}
            onClose={() => setIsBatchModalOpen(false)}
            projects={projects}
            onUpdateProject={onUpdateProject}
        />

       {/* Delete Modal */}
       {projectToDelete && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 dark:bg-black/80 backdrop-blur-sm animate-fade-in">
             <div className="glass-panel p-6 rounded-2xl w-full max-w-sm shadow-2xl relative flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-500/10 flex items-center justify-center text-red-600 dark:text-red-500 mb-4 border border-red-200 dark:border-red-500/20">
                   <AlertTriangle size={24} />
                </div>
                
                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">Delete Project?</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                   Are you sure you want to delete <span className="text-slate-800 dark:text-white font-medium font-mono">Day #{projectToDelete}</span>? 
                   <br/>This action cannot be undone.
                </p>

                <div className="flex w-full gap-3">
                   <button 
                      onClick={() => setProjectToDelete(null)}
                      disabled={isSaving}
                      className="flex-1 py-2.5 rounded-lg border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white transition-all text-sm font-medium disabled:opacity-50"
                   >
                      Cancel
                   </button>
                   <button 
                      onClick={confirmDelete}
                      disabled={isSaving}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg transition-all text-sm font-bold shadow-lg shadow-red-500/20 disabled:opacity-50"
                   >
                      {isSaving ? 'Deleting...' : 'Yes, Delete'}
                   </button>
                </div>
             </div>
          </div>
       )}
    </div>
  );
};

// Simple loader helper import
import { Loader2 } from 'lucide-react';